import { Component, Inject, Optional, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { APP_BASE_HREF, DOCUMENT } from '@angular/common';
import { RuntimeConfigService } from '../service/config/runtime-config.service';
import { LoggerService } from '../service/utils/logger.service';
import { BrandService } from '../service/brand/brand.service';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  templateUrl: './forgot-password.component.html',
  styleUrls: ['../login/login.component.css'],
})
export class ForgotPasswordComponent {
  form: FormGroup;
  submitted = false;
  loading = false;
  message = '';
  error = '';
  protected readonly brand = inject(BrandService);

  constructor(
    private fb: FormBuilder,
    private runtime: RuntimeConfigService,
    @Inject(DOCUMENT) private doc: Document,
    @Optional() @Inject(APP_BASE_HREF) private appBaseHref: string | null,
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  /** Must match an entry in Supabase Dashboard → Authentication → URL Configuration → Redirect URLs */
  private recoveryRedirectUrl(): string {
    const win = this.doc.defaultView;
    if (!win) return '';
    const origin = win.location.origin;
    const base = (this.appBaseHref ?? '/').replace(/\/?$/, '/');
    return `${origin}${base}reset-password`;
  }

  async onSubmit(): Promise<void> {
    this.submitted = true;
    this.message = '';
    this.error = '';
    if (!this.form.valid) return;
    const base = this.runtime.supabaseProjectUrl;
    const apiKey = this.runtime.supabaseApiKey;
    if (!base || !apiKey) {
      this.error =
        'Password recovery is not configured (missing Supabase URL or publishable/anon API key in runtime-config).';
      return;
    }
    const email = String(this.form.value.email || '').trim();
    const redirectTo = this.recoveryRedirectUrl();
    if (!redirectTo) {
      this.error = 'Could not build reset link URL. Try reloading the page.';
      return;
    }
    this.loading = true;
    try {
      const res = await fetch(`${base}/auth/v1/recover`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: apiKey,
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({ email, redirect_to: redirectTo }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        LoggerService.error('Recover failed', { status: res.status, text });
        this.error = 'Could not send recovery email. Try again later.';
        return;
      }
      this.message =
        'If an account exists for this email, you will receive a message with a reset link shortly.';
    } catch (e) {
      LoggerService.error('Recover request error', e);
      this.error = 'Network error. Try again later.';
    } finally {
      this.loading = false;
    }
  }
}
